import React, { useState, useEffect } from 'react';
import style from './index.module.css';
import { useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Rating from '@mui/material/Rating';
import Box from '@mui/material/Box';
import StarIcon from '@mui/icons-material/Star';
import { getFirestore, collection, addDoc, getDocs, query, where, serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { firebaseApp } from '../../utils/firebase';
import CircularProgress from '@mui/material/CircularProgress';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShareIcon from '@mui/icons-material/Share';
import MyFooter from '../../components/MyFooter';
import IconButton from '@mui/material/IconButton';

const ClientStories = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState('idle');
    const [formData, setFormData] = useState({
        name: '',
        eventTitle: '',
        story: '',
        imageUrl: '',
        rating: 5
    });

    useEffect(() => {
        fetchStories();
    }, []);

    const fetchStories = async () => {
        setLoading(true);
        try {
            const db = getFirestore();
            const q = query(collection(db, 'stories'), where('status', '==', 'approved'));
            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setStories(data.sort((a, b) => b.timestamp?.seconds - a.timestamp?.seconds));
        } catch (error) {
            console.error('Error fetching stories:', error);
        } finally {
            setLoading(false);
        }
    };

    const averageRating = stories.length > 0 
        ? (stories.reduce((acc, curr) => acc + (curr.rating || 0), 0) / stories.length).toFixed(1)
        : 0;

    const handleLike = async (storyId, currentLikes) => {
        try {
            const db = getFirestore();
            const storyRef = doc(db, 'stories', storyId);
            await updateDoc(storyRef, { likes: (currentLikes || 0) + 1 });
            setStories(stories.map(s => s.id === storyId ? { ...s, likes: (s.likes || 0) + 1 } : s));
        } catch (error) {
            console.error('Error liking story:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmissionStatus('loading');
        try {
            const db = getFirestore();
            await addDoc(collection(db, 'stories'), {
                ...formData,
                status: 'pending', // Requires admin approval
                likes: 0,
                timestamp: serverTimestamp()
            });
            setSubmissionStatus('success');
            setTimeout(() => {
                setShowForm(false);
                setSubmissionStatus('idle');
                setFormData({ name: '', eventTitle: '', story: '', imageUrl: '', rating: 5 });
            }, 3000);
        } catch (error) {
            console.error('Submission Error:', error.code, error.message);
            setSubmissionStatus('error');
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
                width: '100%',
                minHeight: '100vh',
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
            }}
        >
            <div className={style.container}>
                <div className={style.header}>
                    <p style={{ color: theme.palette.primary.main }}>COMMUNITY</p>
                    <h1>Client Stories</h1>
                    <p className={style.subtitle}>Real experiences from our beautiful celebrations</p>
                    
                    {stories.length > 0 && (
                        <motion.div 
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            className={style.ratingStats}
                        >
                            <div className={style.avgBadge}>
                                <span className={style.avgNumber}>{averageRating}</span>
                                <Rating value={Number(averageRating)} readOnly precision={0.5} 
                                    emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                                />
                                <span className={style.totalReviews}>Based on {stories.length} reviews</span>
                            </div>
                        </motion.div>
                    )}

                    <Button 
                        variant="contained" 
                        onClick={() => setShowForm(!showForm)}
                        sx={{ mt: 3, borderRadius: 10, px: 4 }}
                    >
                        {showForm ? 'Close Form' : 'Share Your Story'}
                    </Button>
                </div>

                <AnimatePresence>
                    {showForm && (
                        <motion.div 
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className={style.formWrapper}
                        >
                            {submissionStatus === 'success' ? (
                                <div className={style.successNotice}>
                                    <h3>Thank You!</h3>
                                    <p>Your story has been submitted for review. It will appear here once approved by our team.</p>
                                </div>
                            ) : (
                                <form className={style.storyForm} onSubmit={handleSubmit}>
                                    <div className={style.formGrid}>
                                        <TextField 
                                            fullWidth label="Your Name" required
                                            value={formData.name} 
                                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        />
                                        <TextField 
                                            fullWidth label="Event Title (e.g. Rahul & Priya's Wedding)" required
                                            value={formData.eventTitle}
                                            onChange={(e) => setFormData({...formData, eventTitle: e.target.value})}
                                        />
                                    </div>
                                    
                                    <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <p style={{ margin: 0, fontWeight: 600 }}>Your Rating:</p>
                                        <Rating
                                            size="large"
                                            value={formData.rating}
                                            onChange={(event, newValue) => {
                                                setFormData({...formData, rating: newValue});
                                            }}
                                        />
                                    </Box>

                                    <TextField 
                                        fullWidth label="Image URL (Link to your event photo)" 
                                        value={formData.imageUrl}
                                        onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                                        sx={{ mt: 2 }}
                                    />
                                    <TextField 
                                        fullWidth multiline rows={4} label="Share your experience..." required
                                        value={formData.story}
                                        onChange={(e) => setFormData({...formData, story: e.target.value})}
                                        sx={{ mt: 2 }}
                                    />
                                    <Button 
                                        type="submit" 
                                        variant="contained" 
                                        disabled={submissionStatus === 'loading'}
                                        sx={{ mt: 3 }}
                                    >
                                        {submissionStatus === 'loading' ? 'Submitting...' : 'Submit Story'}
                                    </Button>
                                    {submissionStatus === 'error' && (
                                        <p style={{ color: theme.palette.error.main, marginTop: '1rem', textAlign: 'center' }}>
                                            Something went wrong. Please try again or check your internet connection.
                                        </p>
                                    )}
                                </form>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className={style.storyFeed}>
                    {loading ? (
                        <CircularProgress />
                    ) : stories.length === 0 ? (
                        <p>No stories shared yet. Be the first!</p>
                    ) : (
                        stories.map((item, index) => (
                            <motion.div 
                                key={item.id}
                                initial={{ y: 50, opacity: 0 }}
                                whileInView={{ y: 0, opacity: 1 }}
                                viewport={{ once: true }}
                                className={style.storyCard}
                                style={{
                                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
                                    boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.4)' : '0 10px 30px rgba(0,0,0,0.05)',
                                }}
                            >
                                {item.imageUrl && (
                                    <img src={item.imageUrl} alt={item.eventTitle} className={style.storyImage} />
                                )}
                                <div className={style.storyContent}>
                                    <div className={style.storyHeader}>
                                        <div>
                                            <h2>{item.eventTitle}</h2>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                                                <p className={style.author}>By {item.name}</p>
                                                <Rating value={item.rating || 5} readOnly size="small" />
                                            </div>
                                        </div>
                                        <div className={style.storyActions}>
                                            <IconButton onClick={() => handleLike(item.id, item.likes)}>
                                                {item.likes > 0 ? <FavoriteIcon color="error" /> : <FavoriteBorderIcon />}
                                                <span className={style.count}>{item.likes || 0}</span>
                                            </IconButton>
                                            <IconButton>
                                                <ShareIcon />
                                            </IconButton>
                                        </div>
                                    </div>
                                    <p className={style.text}>{item.story}</p>
                                    <span className={style.date}>
                                        {item.timestamp?.toDate().toLocaleDateString() || 'Recently'}
                                    </span>
                                </div>
                            </motion.div>
                        ))
                    )}
                </div>
            </div>

            <MyFooter />
        </motion.div>
    );
};

export default ClientStories;
